import { Chip, type ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import { statusColors as fallbackStatusColors } from '@/styles/theme';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import PauseIcon from '@mui/icons-material/Pause';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';

export type WorkOrderStatus =
  | 'quote'
  | 'pending'
  | 'vehicle_arrived'
  | 'in_progress'
  | 'paused'
  | 'waiting_parts'
  | 'work_completed'
  | 'ready_to_invoice'
  | 'invoiced'
  | 'completed'
  | 'cancelled'
  | 'quote_rejected'
  // Legacy values — kept for backward compatibility (status history)
  // 'confirmed' is legacy: confirmation is now the isConfirmed flag (#534)
  | 'confirmed'
  | 'scheduled'
  | 'waiting_approval'
  | 'warranty_claim'
  | 'disputed'
  | 'invoicing_hold';

interface StatusBadgeProps {
  status: WorkOrderStatus;
  size?: ChipProps['size'];
}

/** Map icon name (from flow config) to MUI icon component */
const ICON_COMPONENTS: Record<string, React.ElementType> = {
  Schedule: ScheduleIcon,
  ThumbUpAlt: ThumbUpAltIcon,
  DirectionsCar: DirectionsCarIcon,
  Build: BuildIcon,
  Pause: PauseIcon,
  LocalShipping: LocalShippingIcon,
  TaskAlt: TaskAltIcon,
  ReceiptLong: ReceiptLongIcon,
  CheckCircle: CheckCircleIcon,
  Cancel: CancelIcon,
  RequestQuote: RequestQuoteIcon,
  ThumbDown: ThumbDownIcon,
  ReportProblem: ReportProblemIcon,
  Shield: ShieldIcon,
  HourglassBottom: HourglassBottomIcon,
};

/** Fallback icon map — used when flow config is loading or icon not found */
const FALLBACK_ICON_MAP: Record<WorkOrderStatus, React.ReactElement> = {
  quote: <RequestQuoteIcon sx={{ fontSize: 14 }} />,
  pending: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
  confirmed: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
  vehicle_arrived: <DirectionsCarIcon sx={{ fontSize: 14 }} />,
  in_progress: <BuildIcon sx={{ fontSize: 14 }} />,
  paused: <PauseCircleOutlineIcon sx={{ fontSize: 14 }} />,
  waiting_parts: <LocalShippingIcon sx={{ fontSize: 14 }} />,
  work_completed: <AssignmentTurnedInIcon sx={{ fontSize: 14 }} />,
  ready_to_invoice: <ReceiptLongIcon sx={{ fontSize: 14 }} />,
  invoiced: <ReceiptLongIcon sx={{ fontSize: 14 }} />,
  completed: <CheckCircleIcon sx={{ fontSize: 14 }} />,
  cancelled: <CancelIcon sx={{ fontSize: 14 }} />,
  quote_rejected: <ThumbDownIcon sx={{ fontSize: 14 }} />,
  // Legacy (status history may contain these old values)
  scheduled: <HourglassEmptyIcon sx={{ fontSize: 14 }} />,
  waiting_approval: <AssignmentTurnedInIcon sx={{ fontSize: 14 }} />,
  warranty_claim: <ShieldIcon sx={{ fontSize: 14 }} />,
  disputed: <ReportProblemIcon sx={{ fontSize: 14 }} />,
  invoicing_hold: <HourglassBottomIcon sx={{ fontSize: 14 }} />,
};

function getIconElement(iconName: string | undefined): React.ReactElement | undefined {
  if (!iconName) return undefined;
  const IconComponent = ICON_COMPONENTS[iconName];
  if (!IconComponent) return undefined;
  return <IconComponent sx={{ fontSize: 14 }} />;
}

export function StatusBadge({ status, size = 'small' }: StatusBadgeProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const { statusColors, statusIcons } = useWorkOrderFlow();

  const color = statusColors[status] ?? fallbackStatusColors[status] ?? fallbackStatusColors.pending;
  const statusLabel = t(`status.${status}`);
  const icon = getIconElement(statusIcons[status]) ?? FALLBACK_ICON_MAP[status];
  const statusAria = tc('statusAria', { status: statusLabel });
  const ariaLabel = statusAria.includes('{{status}}')
    ? statusAria.replace('{{status}}', statusLabel)
    : statusAria === 'statusAria'
      ? `Status: ${statusLabel}`
      : statusAria;

  return (
    <Chip
      label={statusLabel}
      icon={icon}
      size={size}
      role="status"
      aria-label={ariaLabel}
      sx={{
        backgroundColor: color,
        color: 'common.white',
        fontWeight: 500,
        '& .MuiChip-icon': {
          color: 'common.white',
        },
      }}
    />
  );
}
